import { Account, Client, Databases, ID, Query, Avatars, Storage } from 'appwrite';

// Appwrite config from environment variables (see .env)
export const appwriteConfig = {
    endpoint: process.env.REACT_APP_APPWRITE_ENDPOINT || "https://cloud.appwrite.io/v1",
    projectId: process.env.REACT_APP_APPWRITE_PROJECT_ID,
    databaseId: process.env.REACT_APP_APPWRITE_DATABASE_ID,
    agsMembersID: process.env.REACT_APP_APPWRITE_AGSMEMBERS_ID,
    bucketId: process.env.REACT_APP_APPWRITE_BUCKET_ID,
    preciosAmateurCollectionId: "preciosamateur",
    giraAmateurCollectionId: "giraamateur",
    giraInfantilCollectionId: "girainfantil",
    clubxEstadoCollectionId: "6a6ac5b300019ba32d99",
};

const client = new Client();
client
    .setEndpoint(appwriteConfig.endpoint)
    .setProject(appwriteConfig.projectId);

const account = new Account(client);
const databases = new Databases(client);
const avatars = new Avatars(client);
const storage = new Storage(client);

// ---- Pricing helpers (Appwrite DB) ----
export async function listPreciosAmateur() {
  const res = await databases.listDocuments(
    appwriteConfig.databaseId,
    appwriteConfig.preciosAmateurCollectionId,
    [Query.limit(200)]
  );
  return res.documents || [];
}

export async function listGiraAmateurEtapasByGira(giraCodigo) {
  const res = await databases.listDocuments(
    appwriteConfig.databaseId,
    appwriteConfig.giraAmateurCollectionId,
    [Query.equal('Gira', Number(giraCodigo)), Query.limit(200)]
  );
  return res.documents || [];
}

/** Cupo eventual: eventMax > 0 y eventIns < eventMax (giraamateur). */
export function getEventualCupoFromEtapaDoc(etapaDoc) {
  if (!etapaDoc) return { disponible: false, eventMax: 0, eventIns: 0 };
  const eventMax = Number(etapaDoc.eventMax) || 0;
  const eventIns = Number(etapaDoc.eventIns) || 0;
  return {
    disponible: eventMax > 0 && eventIns < eventMax,
    eventMax,
    eventIns,
  };
}

export async function getEventualCupoForEtapa(giraCodigo, etapaNumero) {
  const docs = await listGiraAmateurEtapasByGira(giraCodigo);
  const doc = (docs || []).find((d) => Number(d.Etapa) === Number(etapaNumero));
  return getEventualCupoFromEtapaDoc(doc);
}

export async function incrementEventInsForEtapa(giraCodigo, etapaNumero) {
  const docs = await listGiraAmateurEtapasByGira(giraCodigo);
  const doc = (docs || []).find((d) => Number(d.Etapa) === Number(etapaNumero));
  const cupo = getEventualCupoFromEtapaDoc(doc);
  if (!doc || !cupo.disponible) {
    throw new Error('No hay lugares de eventuales disponibles para esta etapa');
  }
  await databases.updateDocument(
    appwriteConfig.databaseId,
    appwriteConfig.giraAmateurCollectionId,
    doc.$id,
    { eventIns: cupo.eventIns + 1 }
  );
}

// ---- Gira Infantil (Appwrite: girainfantil) — mismos campos que giraamateur ----
export async function listGiraInfantilEtapasByGira(giraCodigo) {
  const res = await databases.listDocuments(
    appwriteConfig.databaseId,
    appwriteConfig.giraInfantilCollectionId,
    [Query.equal('Gira', Number(giraCodigo)), Query.limit(200)]
  );
  return res.documents || [];
}

export async function getEventualCupoForEtapaInfantil(giraCodigo, etapaNumero) {
  const docs = await listGiraInfantilEtapasByGira(giraCodigo);
  const doc = (docs || []).find((d) => Number(d.Etapa) === Number(etapaNumero));
  return getEventualCupoFromEtapaDoc(doc);
}

export async function incrementEventInsForEtapaInfantil(giraCodigo, etapaNumero) {
  const docs = await listGiraInfantilEtapasByGira(giraCodigo);
  const doc = (docs || []).find(
    (d) => Number(d.Etapa) === Number(etapaNumero) || String(d.Etapa) === String(etapaNumero)
  );
  if (!doc) {
    throw new Error(
      `No se encontró etapa ${etapaNumero} en girainfantil para gira ${giraCodigo}. Revisa que Etapa coincida con insetapa.`
    );
  }
  const cupo = getEventualCupoFromEtapaDoc(doc);
  if (!cupo.disponible) {
    throw new Error('No hay lugares de eventuales disponibles para esta etapa');
  }
  try {
    await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.giraInfantilCollectionId,
      doc.$id,
      { eventIns: cupo.eventIns + 1 }
    );
  } catch (err) {
    const msg = err?.message || String(err);
    if (/401|unauthorized|not authorized/i.test(msg)) {
      throw new Error(
        'Sin permiso para actualizar eventIns en girainfantil. En Appwrite otorga Update a Users/Any en esa colección.'
      );
    }
    throw err;
  }
}

/** ClubxEstado: clubcodigo → estado (para eventual infantil). */
export async function getEstadoByClubCodigo(clubcodigo) {
  if (clubcodigo == null || clubcodigo === '') return null;
  const res = await databases.listDocuments(
    appwriteConfig.databaseId,
    appwriteConfig.clubxEstadoCollectionId,
    [Query.equal('clubcodigo', Number(clubcodigo)), Query.limit(1)]
  );
  const doc = (res.documents || [])[0];
  if (!doc) return null;
  const estado = doc.estado ?? doc.Estado;
  return estado != null && String(estado).trim() !== '' ? estado : null;
}

/**
 * Eventual permitido solo si el estado del club del niño coincide con el estado de la etapa (girainfantil.estado).
 */
export function canPlayEventualByEstado(clubEstado, etapaDoc) {
  if (clubEstado == null || !etapaDoc) return false;
  const etapaEstado = etapaDoc.estado ?? etapaDoc.Estado;
  if (etapaEstado == null || String(etapaEstado).trim() === '') return false;
  return String(clubEstado).trim() === String(etapaEstado).trim();
}

export async function getEventualEligibilityInfantil(giraCodigo, etapaNumero, clubcodigo) {
  const docs = await listGiraInfantilEtapasByGira(giraCodigo);
  const etapaDoc = (docs || []).find((d) => Number(d.Etapa) === Number(etapaNumero));
  const cupo = getEventualCupoFromEtapaDoc(etapaDoc);
  const clubEstado = await getEstadoByClubCodigo(clubcodigo);
  const estadoOk = canPlayEventualByEstado(clubEstado, etapaDoc);
  return {
    disponible: cupo.disponible === true && estadoOk,
    cupoOk: cupo.disponible === true,
    estadoOk,
    clubEstado,
    etapaEstado: etapaDoc?.estado ?? etapaDoc?.Estado ?? null,
    eventMax: cupo.eventMax,
    eventIns: cupo.eventIns,
  };
}

/**
 * Fecha límite (fin del día local) para el precio tipo "antes del descuento".
 * Usa el campo datetime `Descuento` en giraamateur: debe estar en la primera etapa de cada gira.
 * Si no hay valor válido, devuelve null (el caller aplica fecha por defecto).
 */
export function getDescuentoCutoffEndOfDayFromEtapaDocs(etapaDocs) {
  if (!Array.isArray(etapaDocs) || etapaDocs.length === 0) return null;
  const withDesc = etapaDocs.filter(
    (d) => d.Descuento != null && String(d.Descuento).trim() !== ''
  );
  if (withDesc.length === 0) return null;
  withDesc.sort((a, b) => {
    const ea = Number(a.Etapa);
    const eb = Number(b.Etapa);
    if (Number.isFinite(ea) && Number.isFinite(eb)) return ea - eb;
    if (Number.isFinite(ea)) return -1;
    if (Number.isFinite(eb)) return 1;
    return 0;
  });
  const raw = withDesc[0].Descuento;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(
    parsed.getFullYear(),
    parsed.getMonth(),
    parsed.getDate(),
    23,
    59,
    59,
    999
  );
}

/** Si Appwrite no trae Descuento: 16 de abril, fin de día (año civil actual). */
export function getDefaultDescuentoCutoffEndOfDay(referenceDate = new Date()) {
  const y = referenceDate.getFullYear();
  return new Date(y, 3, 16, 23, 59, 59, 999);
}

// Create New User
export async function createUser(name, email, password) {
    try {
        const newAccount = await account.create(
            ID.unique(),
            email,
            password,
            name
        );

        if (!newAccount) throw Error;

        // Automatically sign in the user after creation
        await signIn(email, password);

        return {
            success: true,
            accountId: newAccount.$id,
            email: email,
            name: name
        };
    } catch (error) {
        console.error('Error creating user:', error);
        throw new Error(error.message || 'Error creating user account');
    }
}

// Helper function to wait
const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Sign In with retry logic
export const signIn = async (email, password) => {
    const maxRetries = 5;
    let retryCount = 0;

    while (retryCount < maxRetries) {
        try {
            // First, try to get current session
            try {
                const currentSession = await account.getSession('current');
                if (currentSession) {
                    // Delete the current session
                    await account.deleteSession('current');
                }
            } catch (error) {
                // If there's no current session, that's fine
                console.log('No active session found');
            }

            // Now create a new session
            const session = await account.createEmailPasswordSession(email, password);
            return session;
        } catch (error) {
            console.error('Sign in attempt error:', error);
            
            if (error.message.includes('Rate limit')) {
                retryCount++;
                if (retryCount < maxRetries) {
                    const waitTime = retryCount * 5000;
                    console.log(`Rate limit hit, retrying in ${waitTime/1000} seconds...`);
                    await wait(waitTime);
                    continue;
                }
            }
            
            throw new Error(error.message || 'Error al iniciar sesión');
        }
    }
    throw new Error('Demasiados intentos fallidos. Por favor, espere unos minutos antes de intentar nuevamente.');
}

// Get Current User Session (Simple Auth Only)
export const getCurrentUser = async () => {
    const maxRetries = 5;
    let retryCount = 0;

    while (retryCount < maxRetries) {
        try {
            const currentAccount = await account.get();

            if (!currentAccount) {
                return null;
            }

            // Return just the account info without database document
            return {
                $id: currentAccount.$id,
                email: currentAccount.email,
                name: currentAccount.name,
                // Add avatar using initials
                avatar: avatars.getInitials(currentAccount.name || currentAccount.email)
            };
        } catch (error) {
            console.error('Error in getCurrentUser:', error);
            if (error.message.includes('Rate limit')) {
                retryCount++;
                if (retryCount < maxRetries) {
                    const waitTime = retryCount * 5000;
                    console.log(`Rate limit hit, retrying in ${waitTime/1000} seconds...`);
                    await wait(waitTime);
                    continue;
                }
            }
            if (error.code === 401) {
                throw new Error('Sesión expirada. Por favor, inicia sesión nuevamente.');
            }
            throw new Error(`Error al obtener la información del usuario: ${error.message}`);
        }
    }
    throw new Error('Demasiados intentos fallidos. Por favor, espere unos minutos antes de intentar nuevamente.');
}



// Logout function
export const logout = async () => {
    try {
        await account.deleteSession('current');
        return true;
    } catch (error) {
        console.error('Error during logout:', error);
        throw new Error('Error al cerrar sesión');
    }
};

//Change Password function
export const UpdatePassword = async (oldPass,newPass) => {
    try {
        await account.updatePassword(
            newPass, // password
            oldPass // oldPassword
        );
        return true;
    } catch (error) {
        console.error('Error during password update:', error);
        throw new Error('Error al cambiar contraseña')
    }
};

/**
 * Request password recovery email (user not logged in).
 * Redirect URL must be registered as a platform in Appwrite Console.
 * @see https://appwrite.io/docs/products/auth/email-password
 */
export const createRecovery = async (email, redirectUrl) => {
    try {
        await account.createRecovery(email, redirectUrl);
        return { success: true };
    } catch (error) {
        console.error('Error requesting password recovery:', error);
        throw new Error(error?.message || 'Error al enviar el correo de recuperación');
    }
};

/**
 * Complete password recovery from the email link (userId + secret in URL).
 * Use when user lands on app from recovery email; separate from UpdatePassword (in-app change).
 * @see https://appwrite.io/docs/products/auth/email-password
 */
export const RecoveryPassword = async (userId, secret, newPassword) => {
    try {
        await account.updateRecovery(userId, secret, newPassword);
        return { success: true };
    } catch (error) {
        console.error('Error completing password recovery:', error);
        throw new Error(error?.message || 'Error al restablecer la contraseña. El enlace puede haber expirado.');
    }
};

//Look for the name of a role holder, and signature
export const agsRoles = async (role) => {
    try {
        const currentHolder = await databases.listDocuments(
            appwriteConfig.databaseId,
            appwriteConfig.agsMembersID,
            [Query.equal('cargo', role)]
        );

        if (currentHolder.documents.length > 0) {
            const holder = currentHolder.documents[0];
            
            // If the holder has a signature file ID, get the URL
            if (holder.firma && holder.firma !== '') {
                try {
                    const signatureUrl = getSignatureUrl(holder.firma);
                    return {
                        ...holder,
                        firma: signatureUrl
                    };
                } catch (error) {
                    console.error('Error getting signature URL:', error);
                    // Return holder without signature URL if there's an error
                    return holder;
                }
            }
            
            return holder;
        }
        return null;
    } catch (error) {
        console.error('Error fetching role holder:', error);
        return null;
    }
};

// Get signature image URL
export const getSignatureUrl = (fileId) => {
    try {
        return storage.getFileView(appwriteConfig.bucketId, fileId);
    } catch (error) {
        console.error('Error getting signature URL:', error);
        return null;
    }
};
